import React, { useEffect, useMemo, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import { useCurrency } from "@/context/CurrencyContext";
import { Calculator, ArrowRight, Sparkles } from "lucide-react";
import SourceRibbon from "@/components/market/SourceRibbon";
import { api } from "@/lib/api";
import { arDigits, fixArThousands } from "@/lib/utils";

const fmt = (n, isAR = false) => {
  const locale = isAR ? "ar-EG" : "en-US";
  const s = new Intl.NumberFormat(locale, { maximumFractionDigits: 0 }).format(Math.max(0, n || 0));
  return isAR ? fixArThousands(s) : s;
};

// The Central Bank of the UAE has pegged AED to USD at 3.6725 since 1997,
// so USD conversion here is deterministic — no live rate needed for the
// EMI math to stay stable. We still read the live rate from
// CurrencyContext when it's already been fetched (falls back to the peg).
const AED_PER_USD_PEG = 3.6725;
// CBUAE maximum mortgage tenure.
const MAX_TENURE = 25;

export default function MortgageCalculator({ onApply }) {
  const { t, isRTL, lang } = useLang();
  const { rates } = useCurrency();
  // Local AED/USD toggle. Independent of the global site currency picker
  // because CBUAE LTV rules (80% residents / 50% non-residents / property
  // price cap AED 5M) are defined in AED — we always compute the loan +
  // EMI in AED internally and simply render the display in the chosen
  // currency. Persist so a returning USD-first visitor doesn't have to
  // re-toggle every session.
  const [mcCurrency, setMcCurrency] = useState(() => {
    try { return localStorage.getItem("propertic_mc_currency") || "AED"; }
    catch { return "AED"; }
  });
  useEffect(() => {
    try { localStorage.setItem("propertic_mc_currency", mcCurrency); } catch { /* silent */ }
  }, [mcCurrency]);
  // AED→USD ratio (live rate if CurrencyContext already fetched, otherwise
  // the peg). Never zero — we clamp so the input never divides by 0.
  const usdPerAed = Math.max(rates?.USD || (1 / AED_PER_USD_PEG), 1e-6);
  const aedPerUsd = 1 / usdPerAed;
  const isUSD = mcCurrency === "USD";
  const currencyLabel = isUSD ? (isRTL ? "دولار" : "USD") : (isRTL ? "درهم" : "AED");
  const currencyLabelPerMo = isUSD
    ? (isRTL ? "دولار/شهرياً" : "USD/mo")
    : (isRTL ? "درهم/شهرياً" : "AED/mo");
  // Toggle-pill labels: show Arabic script in RTL, English in LTR.
  const toggleLabel = (c) => (isRTL ? (c === "USD" ? "دولار" : "درهم") : c);
  // Convert any AED amount into the display currency.
  const toDisp = (aed) => (isUSD ? aed * usdPerAed : aed);
  // Convert a raw user input value (in current display currency) back to AED.
  const fromDispToAed = (v) => (isUSD ? Number(v || 0) * aedPerUsd : Number(v || 0));

  const [price, setPrice] = useState(2000000); // always stored in AED
  const [dp, setDp] = useState(25);
  const [tenure, setTenure] = useState(25);
  const [tenureErr, setTenureErr] = useState(false);
  const [rate, setRate] = useState(3.9);
  const [rateMeta, setRateMeta] = useState({
    as_of: "August 2026",
    source: "CBUAE base rate + EIBOR 3M + typical bank margin",
    source_url: "https://www.centralbank.ae/en/forex-eibor/eibor-rates/",
  });

  // Pull the current indicative UAE rate on mount so the default value
  // tracks the CBUAE / EIBOR benchmark instead of being pinned to 4.5%.
  useEffect(() => {
    api.get("/rates/mortgage").then((r) => {
      if (typeof r.data?.rate_pct === "number") {
        setRate(r.data.rate_pct);
        setRateMeta({
          as_of: r.data.as_of,
          source: r.data.source,
          source_url: r.data.source_url,
        });
      }
    }).catch(() => { /* stay on default 3.9 if network blocked */ });
  }, []);

  const { loan, emi, totalInterest, totalPayable } = useMemo(() => {
    const p = Math.max(0, Number(price) || 0);
    const d = Math.min(90, Math.max(0, Number(dp) || 0));
    const y = Math.min(MAX_TENURE, Math.max(1, Number(tenure) || 1));
    const r = Math.max(0, Number(rate) || 0);
    const loanAmt = p * (1 - d / 100);
    const n = y * 12;
    const monthlyRate = r / 100 / 12;
    let e = 0;
    if (monthlyRate === 0) e = loanAmt / n;
    else e = (loanAmt * monthlyRate * Math.pow(1 + monthlyRate, n)) / (Math.pow(1 + monthlyRate, n) - 1);
    const total = e * n;
    return { loan: loanAmt, emi: e, totalInterest: total - loanAmt, totalPayable: total };
  }, [price, dp, tenure, rate]);

  return (
    <section id="calculator" className="py-8 sm:py-16 lg:py-20 relative overflow-hidden">
      <div className="blob-navy w-[600px] h-[600px] -top-40 -start-20 opacity-40" />
      <div className="mx-auto max-w-[1480px] px-3 sm:px-6 lg:px-8 grid lg:grid-cols-[1fr_1.1fr] gap-4 sm:gap-12 lg:gap-16 items-start relative">
        <div>
          <p className="font-mono-brand text-propertic-gold mb-2 sm:mb-4 text-[10px] tracking-[0.14em]">{t("calculator.eyebrow")}</p>
          <motion.h2
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
            className="font-editorial text-[20px] sm:text-3xl leading-[1.1] sm:leading-[1.15] text-propertic-ink"
          >
            {t("calculator.title_pre")} <span className="font-italic-serif text-propertic-gold">{t("calculator.title_mid")}</span> {t("calculator.title_post")}
          </motion.h2>
          <p className="hidden sm:block mt-6 text-propertic-ink-2/95 max-w-md text-lg leading-[1.7]">{t("calculator.subtitle")}</p>

          <div className="mt-4 sm:mt-12 space-y-3 sm:space-y-7" data-testid="mortgage-calculator">
            {/* Property-price row with inline AED/USD toggle so overseas
                visitors can enter the price in their own currency. Math
                stays in AED under the hood; toggle only converts the
                displayed input value + all output labels. */}
            <div>
              <div className="flex items-center justify-between gap-3 mb-1 sm:mb-2">
                <span className="font-mono-brand text-propertic-muted text-[9px] sm:text-[10px] tracking-[0.14em] uppercase">
                  {isUSD
                    ? t("calculator.price").replace(/AED|درهم/gi, isRTL ? "دولار" : "USD")
                    : t("calculator.price")}
                </span>
                <div
                  className="inline-flex items-center rounded-full border hairline bg-white/70 p-0.5 text-[10px] sm:text-[11px] font-mono-brand tracking-[0.14em] uppercase"
                  data-testid="mc-currency-toggle"
                >
                  {["AED", "USD"].map((c) => (
                    <button
                      key={c}
                      type="button"
                      onClick={() => setMcCurrency(c)}
                      data-testid={`mc-currency-${c.toLowerCase()}`}
                      aria-pressed={mcCurrency === c}
                      className={`px-2.5 sm:px-3 py-1 rounded-full transition-colors ${
                        mcCurrency === c
                          ? "bg-propertic-navy text-propertic-paper"
                          : "text-propertic-muted hover:text-propertic-navy"
                      }`}
                    >
                      {toggleLabel(c)}
                    </button>
                  ))}
                </div>
              </div>
              <Field
                testid="mc-price"
                value={Math.round(toDisp(price))}
                onChange={(v) => setPrice(fromDispToAed(v))}
                min={isUSD ? 25000 : 100000}
                step={isUSD ? 10000 : 50000}
                type="number"
                prefix={currencyLabel}
                hideLabel
                isRTL={isRTL}
              />
            </div>
            <RangeField label={t("calculator.down")} testid="mc-down" value={dp} onChange={setDp} min={0} max={90} step={1} displayValue={`${isRTL ? arDigits(dp) : dp}%`} />
            <div className="grid grid-cols-2 gap-3 sm:gap-5">
              <Field label={t("calculator.tenure")} testid="mc-tenure" value={tenure} onChange={(v) => { const n = Number(v); setTenureErr(n > MAX_TENURE ? "max" : (v !== "" && n > 0 && n < 1) ? "min" : false); setTenure(n > MAX_TENURE ? MAX_TENURE : v); }} type="number" min={1} max={MAX_TENURE} isRTL={isRTL} />
              <Field label={t("calculator.rate")} testid="mc-rate" value={rate} onChange={setRate} type="number" min={0.5} step={0.1} suffix={isRTL ? "٪" : "%"} isRTL={isRTL} />
            </div>
            {tenureErr && (
              <p data-testid="mc-tenure-error" className="text-[11px] text-red-600 -mt-1">
                {tenureErr === "min"
                  ? (isRTL ? "أقل مدة يمكن حسابها سنة واحدة؛ النتائج معروضة لمدة سنة." : "The minimum term is 1 year; results are shown for 1 year.")
                  : isRTL
                  ? "الحد الأقصى لمدة قرض الرهن العقاري هو ٢٥ سنة وفقاً لأنظمة مصرف الإمارات العربية المتحدة المركزي."
                  : "Maximum mortgage tenure is 25 years under UAE Central Bank regulations."}
              </p>
            )}
          </div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.9 }}
          className="card-soft rounded-2xl sm:rounded-[2.25rem] p-4 sm:p-8 lg:p-12 relative overflow-hidden mt-4 sm:mt-0"
          data-testid="mortgage-result"
        >
          <div className="absolute -top-16 -end-16 w-56 h-56 rounded-full bg-propertic-gold/8 blur-3xl" />
          <div className="relative">
            <div className="flex items-start justify-between mb-3 sm:mb-10">
              <div>
                <p className="font-mono-brand text-propertic-gold text-[9px] sm:text-[10px] tracking-[0.14em]">{isRTL ? "تقديري" : "Estimated"}</p>
                <p className="font-editorial text-lg sm:text-2xl text-propertic-navy mt-1 sm:mt-2">{t("calculator.emi")}</p>
              </div>
              <div className="w-9 h-9 sm:w-14 sm:h-14 rounded-xl sm:rounded-2xl bg-propertic-sand grid place-items-center text-propertic-gold">
                <Calculator className="w-4 h-4 sm:w-5 sm:h-5" />
              </div>
            </div>
            <div className={`font-editorial text-propertic-navy leading-none tabular ${isRTL ? "" : "force-ltr"}`}>
              <AnimatePresence mode="popLayout">
                <motion.span
                  key={`${mcCurrency}-${Math.round(toDisp(emi) / 100)}`}
                  initial={{ opacity: 0, y: 12, filter: "blur(6px)" }}
                  animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
                  exit={{ opacity: 0, y: -12, filter: "blur(6px)" }}
                  transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
                  className="text-3xl sm:text-5xl inline-block"
                  data-testid="mc-emi-value"
                >
                  {fmt(toDisp(emi), isRTL)}
                </motion.span>
              </AnimatePresence>
              <span className="text-xs sm:text-base text-propertic-muted ms-2 sm:ms-3 font-sans">{currencyLabelPerMo}</span>
            </div>

            {/* "Your monthly is worth", emotional anchoring strip. Numbers
                are rough real-world Dubai references (Atlantis suite night,
                Range Rover monthly lease, Emirates biz KSA↔DXB). Purely
                editorial, keeps the number relatable for overseas visitors. */}
            {emi >= 3000 && (
              <div className="mt-3 sm:mt-4 flex flex-wrap items-center gap-x-3 gap-y-1 text-[10px] sm:text-[11px] text-propertic-muted" data-testid="mc-worth-strip">
                <Sparkles className="w-3 h-3 text-propertic-gold" />
                <span className="font-mono-brand tracking-[0.14em] uppercase text-propertic-gold">{isRTL ? "يعادل شهرياً" : "That's worth"}</span>
                {(() => {
                  const worth = [];
                  const atlantisNight = 3200;
                  const rangeRoverLease = 8500;
                  const emiratesBizFlight = 12000;
                  const nights = Math.round(emi / atlantisNight);
                  if (nights >= 1) worth.push(isRTL ? `${nights} ليلة في أتلانتس` : `${nights} nights at Atlantis`);
                  const leases = (emi / rangeRoverLease).toFixed(1);
                  if (Number(leases) >= 0.4) worth.push(isRTL ? `${leases} إيجار Range Rover` : `${leases}× Range Rover lease`);
                  const flights = (emi / emiratesBizFlight).toFixed(1);
                  if (Number(flights) >= 0.3) worth.push(isRTL ? `${flights} تذكرة رجال أعمال دبي↔الرياض` : `${flights}× Emirates Biz DXB↔RUH`);
                  return worth.slice(0, 3).map((w, i) => (
                    <span key={i} className="inline-flex items-center gap-2">
                      {i > 0 && <span className="opacity-40">·</span>}
                      <span>{w}</span>
                    </span>
                  ));
                })()}
              </div>
            )}

            <div className="grid grid-cols-3 gap-2 sm:gap-5 mt-3 sm:mt-12 pt-3 sm:pt-8 border-t hairline">
              <Stat label={t("calculator.loan_amount")} value={fmt(toDisp(loan), isRTL)} currencyLabel={currencyLabel} isRTL={isRTL} />
              <Stat label={t("calculator.total_interest")} value={fmt(toDisp(totalInterest), isRTL)} currencyLabel={currencyLabel} isRTL={isRTL} />
              <Stat label={t("calculator.total_payable")} value={fmt(toDisp(totalPayable), isRTL)} currencyLabel={currencyLabel} isRTL={isRTL} />
            </div>

            {/* Upfront-cash triad. Down payment is exact (from user input),
                but DLD transfer fee (4%), broker fee (2%) and trustee /
                registration fees are estimates we cannot know precisely
                per-deal — so we surface the down-payment cleanly and note
                the exclusions right underneath. Keeps the calculator
                honest without pretending to be a bank offer. */}
            <div className="mt-3 sm:mt-5 rounded-xl border hairline bg-propertic-sand/40 p-3 sm:p-4" data-testid="mc-upfront-block">
              <div className="flex items-baseline justify-between gap-3">
                <p className="font-mono-brand text-[9px] sm:text-[10px] tracking-[0.16em] uppercase text-propertic-muted">
                  {isRTL ? "الدفعة المقدّمة (تقديري)" : "Down payment (estimated)"}
                </p>
                <p className="font-editorial text-lg sm:text-xl text-propertic-navy tabular force-ltr">
                  {fmt(toDisp(Math.max(0, (Number(price) || 0) - loan)), isRTL)} <span className="text-[10px] sm:text-[11px] text-propertic-muted font-sans">{currencyLabel}</span>
                </p>
              </div>
              <p className="mt-2 text-[10px] sm:text-[11px] text-propertic-muted leading-relaxed">
                {isRTL
                  ? "لا يشمل رسوم دائرة الأراضي (٤٪) وعمولة الوسيط (٢٪) وأتعاب الأمين ورسوم التسجيل. تُحدَّد بدقّة عبر مستشار مرخّص عند إعداد العرض."
                  : "Excludes DLD transfer fee (4%), broker fee (2%), trustee and registration fees. A licensed advisor confirms the exact all-in figure when the offer is prepared."}
              </p>
            </div>

            <button
              type="button"
              onClick={() => onApply?.({ loan_amount_aed: Math.round(loan), loan_tenure_years: tenure })}
              data-testid="mc-apply-btn"
              className="mt-3 sm:mt-8 bg-propertic-navy hover:bg-propertic-navy-2 text-propertic-paper rounded-full w-full h-10 sm:h-14 font-mono-brand text-[10px] sm:text-[11px] tracking-[0.14em] uppercase inline-flex items-center justify-center gap-2 sm:gap-3 transition-colors"
            >
              {t("calculator.apply")}
              <ArrowRight className={`w-4 h-4 ${isRTL ? "rtl-mirror" : ""}`} />
            </button>

            <p className="mt-3 text-[10px] sm:text-[11px] text-propertic-muted leading-snug" data-testid="mc-rate-source">
              {isRTL ? "المعدّل التوضيحي = " : "Illustrative rate = "}<span className="tabular force-ltr">EIBOR 3M + {isRTL ? "هامش بنك افتراضي" : "assumed bank margin"}</span>, <a href={rateMeta.source_url} target="_blank" rel="noreferrer" className="underline hover:text-propertic-gold">{rateMeta.source}</a> · {rateMeta.as_of} · <span className="tabular force-ltr">{isRTL ? `${arDigits(rate)}٪` : `${rate}%`}</span>
            </p>
            <p className="mt-1.5 text-[10px] sm:text-[11px] text-propertic-muted leading-relaxed font-sans italic" data-testid="mc-illustrative-disclaimer">
              {isRTL
                ? "تقدير توضيحي فقط، ليس عرضاً بنكياً، تُحدَّد الأسعار والأهلية من قِبل البنك عند الموافقة."
                : "Illustrative estimate only. Not a bank quotation, actual rates and eligibility are set by your lender at approval."}
            </p>
          </div>
        </motion.div>
      </div>
    </section>
  );
}

function Field({ label, testid, prefix, suffix, value, onChange, type, min, max, step, hideLabel, isRTL = false, ...rest }) {
  // Two-tier state: the parent owns a NUMBER (used for math), the input owns
  // a STRING (used for display + editing). In AR mode the display string
  // is rendered in Eastern Arabic-Indic digits (٠-٩) while the underlying
  // parsed number stays Latin so all math and Intl formatters keep working.
  const isNumeric = type === "number";
  // Helpers scoped to this field — Latin ⇄ Arabic-Indic conversion.
  const AR_DIGITS = "٠١٢٣٤٥٦٧٨٩";
  const toArIndic = (s) => String(s).replace(/[0-9]/g, (d) => AR_DIGITS[+d]);
  // Accept both Arabic-Indic (٠-٩) and Persian-Indic (۰-۹) digits on input.
  const toLatin = (s) => String(s)
    .replace(/[٠-٩]/g, (d) => String("٠١٢٣٤٥٦٧٨٩".indexOf(d)))
    .replace(/[۰-۹]/g, (d) => String("۰۱۲۳۴۵۶۷۸۹".indexOf(d)));
  const localizeDisplay = (raw) => (isRTL ? toArIndic(raw) : raw);

  const [display, setDisplay] = React.useState(value === 0 ? "" : localizeDisplay(String(value)));

  // Two independent sync effects so a pure LANGUAGE toggle re-localizes the
  // display even when the parent numeric `value` is unchanged. The old
  // combined effect short-circuited on `parsed !== value`, so switching
  // AR → EN (or EN → AR) left the input stuck in the previous locale's
  // digits — the exact "EN calculator still showing ٢٠٠٠٠٠٠" bug reported
  // by the user.

  // 1) When parent `value` diverges from what the string represents (e.g.
  //    rate refetched from CBUAE endpoint or AED/USD toggle), reset the
  //    display in the CURRENT locale.
  React.useEffect(() => {
    const parsed = display === "" ? 0 : Number(toLatin(display));
    if (parsed !== value) setDisplay(value === 0 ? "" : localizeDisplay(String(value)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [value]);

  // 2) When ONLY the language flips, re-render the digits in the new
  //    locale but keep whatever the user has typed (parsed value stays
  //    identical, so no math side-effect).
  React.useEffect(() => {
    const parsed = display === "" ? 0 : Number(toLatin(display));
    if (parsed === 0 && display === "") return;
    setDisplay(localizeDisplay(String(parsed)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isRTL]);

  return (
    <label className="block">
      {!hideLabel && (
        <div className="flex items-center justify-between mb-1 sm:mb-2">
          <span className="font-mono-brand text-propertic-muted text-[9px] sm:text-[10px] tracking-[0.14em] uppercase">{label}</span>
        </div>
      )}
      <div className="flex items-center gap-2 bg-transparent border-0 border-b border-propertic-ink/20 rounded-none px-0 h-9 sm:h-12 focus-within:border-propertic-gold transition-colors">
        {prefix && <span className="font-mono-brand text-propertic-muted text-[10px] sm:text-xs">{prefix}</span>}
        <input
          {...rest}
          type="text"
          inputMode={isNumeric ? (step && String(step).includes(".") ? "decimal" : "numeric") : "text"}
          pattern={isNumeric ? "[0-9\\u0660-\\u0669\\u06F0-\\u06F9]*\\.?[0-9\\u0660-\\u0669\\u06F0-\\u06F9]*" : undefined}
          onFocus={(e) => e.target.select()}
          onChange={(e) => {
            // 1) Normalize Arabic-Indic / Persian-Indic digits back to Latin.
            let raw = toLatin(e.target.value)
              .replace(/\u066B/g, ".")          // Arabic decimal separator ٫
              .replace(/[\u066C\s]/g, "");       // Arabic thousands separator ٬ / spaces
            // "3,9" in a decimal field is a decimal comma; elsewhere commas group thousands.
            const isDecimalField = step && String(step).includes(".");
            raw = isDecimalField && /^\d+,\d{0,2}$/.test(raw) ? raw.replace(",", ".") : raw.replace(/,/g, "");
            // 2) Strip anything that isn't a Latin digit or a single dot.
            raw = raw.replace(/[^0-9.]/g, "");
            // Collapse multiple leading zeros: "0500" → "500", but preserve
            // "0.xx" and a lone "0".
            if (/^0\d/.test(raw)) raw = raw.replace(/^0+/, "");
            // Keep only the first decimal point.
            const firstDot = raw.indexOf(".");
            if (firstDot !== -1) raw = raw.slice(0, firstDot + 1) + raw.slice(firstDot + 1).replace(/\./g, "");
            setDisplay(localizeDisplay(raw));
            onChange(raw === "" || raw === "." ? 0 : Number(raw));
          }}
          value={display}
          data-testid={testid}
          className="w-full bg-transparent outline-none text-propertic-ink text-lg sm:text-2xl font-editorial tabular"
        />
        {suffix && <span className="font-mono-brand text-propertic-muted text-[10px] sm:text-xs">{suffix}</span>}
      </div>
    </label>
  );
}

function RangeField({ label, testid, displayValue, value, onChange, ...rest }) {
  return (
    <label className="block">
      <div className="flex items-center justify-between mb-1 sm:mb-3">
        <span className="font-mono-brand text-propertic-muted text-[9px] sm:text-[10px] tracking-[0.14em] uppercase">{label}</span>
        <span className="font-editorial text-lg sm:text-2xl text-propertic-gold tabular">{displayValue}</span>
      </div>
      <input
        type="range"
        value={value}
        onChange={(e) => onChange(Number(e.target.value))}
        data-testid={testid}
        className="slider-gold w-full"
        {...rest}
      />
    </label>
  );
}

function Stat({ label, value, currencyLabel, isRTL: statIsRTL }) {
  const { isRTL: langRTL } = useLang();
  const isRTL = statIsRTL !== undefined ? statIsRTL : langRTL;
  // Same bidi rule as MarketWidgets: in RTL let the paragraph flow naturally
  // so the digit cluster (bidi class AN) anchors at the right edge under the
  // label, with the currency word (درهم) floating to its left. `force-ltr`
  // inverted this and pushed درهم under the label, throwing the digits into
  // the neighbouring column's air.
  return (
    <div className="flex flex-col">
      <p className="font-mono-brand text-propertic-muted mb-0.5 sm:mb-1.5 text-[9px] sm:text-[10px] tracking-[0.14em] leading-none m-0">{label}</p>
      <p className={`text-propertic-ink text-[13px] sm:text-base font-editorial tabular leading-none m-0 ${isRTL ? "" : "force-ltr"}`}>
        {value} <span className="text-[9px] sm:text-[10px] text-propertic-muted font-sans">{currencyLabel || (isRTL ? "درهم" : "AED")}</span>
      </p>
    </div>
  );
}
